import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Send, Wallet, CheckCircle2, Pencil, Receipt, PlugZap, Trash2, MailWarning } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { usePermission } from '../../../core/auth/usePermission';
import { formatDate, formatMoney } from '../../../design-system/format';
import { VISUAL } from '../../../design-system/visual';
import { StatusBadge } from '../components/StatusBadge';
import { InvoiceActions } from '../components/InvoiceActions';
import { PdfPreviewPanel } from '../components/PdfPreviewPanel';
import { RecordPaymentDialog } from '../components/RecordPaymentDialog';
import { CreditNoteDialog } from '../components/CreditNoteDialog';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { invoicesApi } from '../api';
import { ReleaseBilledTimeCard } from '../../projects/components/ReleaseBilledTimeCard';
import { quickbooksApi } from '../../integrations/api';
import { useQuickBooksSync } from '../../integrations/useQuickBooksSync';
import { confirmRePush, reportPushResult } from '../../integrations/pushFeedback';
import { amountDue, type Client, type Invoice, type InvoicePayment, type InvoiceWithItems } from '../types';

const DOC_LABEL: Record<string, string> = { invoice: 'Invoice', estimate: 'Estimate', credit_note: 'Credit note' };

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { tenant } = useBranding();
  const tenantCurrency = (tenant as any)?.currency ?? 'USD';
  const invQ = useApi<{ invoice: InvoiceWithItems }>(id ? `/api/invoicing/invoices/${id}` : null, [id]);
  const [invoice, setInvoice] = React.useState<InvoiceWithItems | null>(null);
  const [payDialogOpen, setPayDialogOpen] = React.useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = React.useState(false);
  const canSend = usePermission('invoices.send');
  const canWrite = usePermission('invoices.write');
  const [deletingPaymentId, setDeletingPaymentId] = React.useState<string | null>(null);
  const confirm = useConfirm();
  const qbo = useQuickBooksSync('invoice', canSend && id ? [id] : []);
  const qboPushed = id ? qbo.isPushed(id) : false;
  const [pushingToQbo, setPushingToQbo] = React.useState(false);

  React.useEffect(() => { if (invQ.data) setInvoice(invQ.data.invoice); }, [invQ.data]);
  const clientQ = useApi<{ client: Client }>(invoice ? `/api/invoicing/clients/${invoice.client_id}` : null);

  const docType = invoice?.document_type ?? 'invoice';
  const currency = invoice?.currency ?? tenantCurrency;
  const hasMoneyApplied = invoice != null
    && (Number(invoice.amount_paid ?? 0) > 0 || Number(invoice.amount_credited ?? 0) > 0);

  // Payments ledger — only meaningful for invoices that have/can have payments.
  const paymentsQ = useApi<{ payments: InvoicePayment[] }>(
    invoice && docType === 'invoice' && invoice.status !== 'draft'
      ? `/api/invoicing/invoices/${invoice.id}/payments`
      : null,
    [invoice?.id, invoice?.status, invoice?.amount_paid],
  );
  const payments = paymentsQ.data?.payments ?? [];

  /**
   * The undo path for a mistaken "Mark paid" / recorded payment. Only manual
   * entries can go; a gateway payment 409s with `provider_payment`, whose
   * message ("issue a refund instead") is the actionable bit — so it is shown
   * verbatim rather than swallowed by a generic failure toast.
   */
  async function handleDeletePayment(p: InvoicePayment) {
    if (!invoice) return;
    const ok = await confirm({
      title: 'Delete this payment?',
      description: `${formatMoney(p.amount, currency)} comes off this invoice and its balance is recalculated. Only payments recorded by hand can be deleted — card and bank-gateway payments need a refund instead.`,
      destructive: true,
      confirmLabel: 'Delete payment',
    });
    if (!ok) return;
    setDeletingPaymentId(p.id);
    try {
      const next = await invoicesApi.deletePayment(invoice.id, p.id);
      setInvoice(next);
      paymentsQ.reload?.();
      toast.success('Payment deleted', { description: 'The invoice balance has been recalculated.' });
    } catch (err: any) {
      toast.error(
        err?.code === 'provider_payment' ? 'This payment needs a refund' : 'Could not delete payment',
        { description: err.message },
      );
    } finally {
      setDeletingPaymentId(null);
    }
  }

  async function handlePushToQuickBooks() {
    if (!invoice) return;
    // Only a re-push can duplicate, so only a re-push asks first.
    if (qboPushed && !(await confirmRePush(confirm, 'invoice'))) return;
    setPushingToQbo(true);
    try {
      const res = qboPushed
        ? await quickbooksApi.pushInvoice(invoice.id, { force: true })
        : await quickbooksApi.pushInvoice(invoice.id);
      reportPushResult(res);
      qbo.markPushed(invoice.id);
    } catch (err: any) {
      toast.error('Push to QuickBooks failed', { description: err.message });
    } finally {
      setPushingToQbo(false);
    }
  }

  if (invQ.loading || !invoice) return <LoadingState variant="page" />;
  const client = clientQ.data?.client;
  const due = amountDue(invoice);
  const isSettledView = invoice.status === 'paid' || invoice.status === 'cancelled';
  // Only show "amount due" arithmetic while the invoice is actually open —
  // a manually marked-paid invoice is settled regardless of its ledger.
  const showDueMath = docType === 'invoice' && hasMoneyApplied && !isSettledView;

  /**
   * `sent_at` is DELIVERY truth — the API stamps it only once a message was
   * actually handed to the mailer. So an OPEN document with no `sent_at` was
   * issued but never emailed (the recurring auto-send produces this when the
   * client has no email on file, and the old draft-only send left rows like
   * it behind). Say so, rather than showing a confident "Sent" badge over an
   * email that never went out.
   */
  const notDelivered =
    docType !== 'credit_note'
    && !invoice.sent_at
    && (invoice.status === 'sent' || invoice.status === 'overdue' || invoice.status === 'partial');
  const clientHasNoEmail = clientQ.data != null && !client?.email;

  return (
    <div className="flex h-full min-h-screen">
      <div className="flex-1 min-w-0 overflow-auto">
        <PageHeader
          eyebrow={<><Link to="/dashboard/invoices" className="hover:text-foreground transition-colors">Invoices</Link> / {invoice.invoice_number}</>}
          title={client?.name ?? invoice.invoice_number}
          chip={<StatusBadge status={invoice.status} />}
          description={
            <span>
              {DOC_LABEL[docType]} <span className="font-mono">{invoice.invoice_number}</span> · issued {formatDate(invoice.issued_at)}
              {invoice.currency ? <> · <span className="font-mono">{invoice.currency}</span></> : null}
            </span>
          }
          actions={
            <div className="flex items-center gap-2">
              {invoice.status === 'draft' && (
                <Link to={`/dashboard/invoices/${invoice.id}/edit`}>
                  <Button variant="outline" className="rounded-full h-10 gap-1.5">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                </Link>
              )}
              {docType === 'invoice' && qbo.canPush && (
                <Button
                  variant="outline"
                  className="rounded-full h-10 gap-1.5"
                  onClick={handlePushToQuickBooks}
                  disabled={pushingToQbo}
                >
                  <PlugZap className="h-3.5 w-3.5" />
                  {pushingToQbo ? 'Pushing…' : qboPushed ? 'Push again to QuickBooks' : 'Push to QuickBooks'}
                </Button>
              )}
              <InvoiceActions
                invoice={invoice as Invoice}
                onChanged={(next) => setInvoice({ ...invoice, ...next })}
                onRecordPayment={() => setPayDialogOpen(true)}
                onCreateCreditNote={() => setCreditDialogOpen(true)}
              />
            </div>
          }
        />

        <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6 max-w-5xl">
          {/* Issued but never delivered — the honest read of a null sent_at */}
          {notDelivered && (
            <section
              role="status"
              className="flex items-start gap-3 rounded-2xl px-5 py-4 ring-1 ring-amber-500/30 bg-amber-500/10 dark:bg-amber-400/10 dark:ring-amber-400/30"
            >
              <MailWarning className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-amber-800 dark:text-amber-200">
                  Not delivered — {DOC_LABEL[docType].toLowerCase()} {invoice.invoice_number} was never emailed to the client
                </p>
                <p className="text-[12.5px] text-amber-800/80 dark:text-amber-200/75 mt-1">
                  {clientHasNoEmail
                    ? <>{client?.name ?? 'This client'} has no email address on file. Add one on the client record, then use Resend.</>
                    : <>The document is issued and its number is final. Use Resend to email it now.</>}
                </p>
                {clientHasNoEmail && client && (
                  <Link
                    to={`/dashboard/clients/${client.id}/edit`}
                    className="inline-block mt-2 text-[12.5px] font-semibold text-amber-900 dark:text-amber-100 underline underline-offset-4 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                  >
                    Add an email address →
                  </Link>
                )}
              </div>
            </section>
          )}

          {/* Cancelled invoices hold their tracked time hostage until it is
              released — the undo half of invoice-from-time (unbill-time). */}
          {docType === 'invoice' && invoice.status === 'cancelled' && (
            <ReleaseBilledTimeCard invoiceId={invoice.id} invoiceNumber={invoice.invoice_number} />
          )}

          {/* Hero number + timeline */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 110, damping: 22 }}
            className={[VISUAL.bento, 'p-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-end'].join(' ')}
          >
            <div>
              <p className={[VISUAL.eyebrow, 'mb-2'].join(' ')}>
                {docType === 'estimate' ? 'Estimate total' : docType === 'credit_note' ? 'Credit amount' : showDueMath ? 'Amount due' : 'Amount'}
              </p>
              <p className={VISUAL.dashBigNumber}>
                {showDueMath ? formatMoney(due, currency) : formatMoney(invoice.total, currency)}
              </p>
              <p className="text-[13px] text-muted-foreground mt-2">
                {invoice.status === 'paid'
                  ? `Paid ${formatDate(invoice.paid_at)}`
                  : invoice.status === 'cancelled'
                    ? 'Cancelled'
                    : invoice.status === 'partial'
                      ? `${formatMoney(invoice.amount_paid ?? 0, currency)} received · due ${formatDate(invoice.due_at)}`
                      : invoice.status === 'accepted'
                        ? 'Accepted by client'
                        : invoice.status === 'declined'
                          ? 'Declined by client'
                          : docType === 'estimate'
                            ? `Valid until ${formatDate(invoice.due_at)}`
                            : `Due ${formatDate(invoice.due_at)}`}
              </p>
              {docType === 'invoice' && hasMoneyApplied && (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-[12.5px] text-muted-foreground">
                  <span>Total <span className="font-mono text-foreground">{formatMoney(invoice.total, currency)}</span></span>
                  {Number(invoice.amount_paid ?? 0) > 0 && (
                    <span>Paid <span className="font-mono text-foreground">−{formatMoney(invoice.amount_paid!, currency)}</span></span>
                  )}
                  {Number(invoice.amount_credited ?? 0) > 0 && (
                    <span>Credited <span className="font-mono text-foreground">−{formatMoney(invoice.amount_credited!, currency)}</span></span>
                  )}
                </div>
              )}
            </div>
            {docType !== 'estimate' ? (
              <ol className="flex items-center gap-0 self-stretch lg:self-end">
                <Step icon={<Pencil className="h-3.5 w-3.5" />} label="Created" date={formatDate(invoice.issued_at)} done />
                <StepDivider />
                <Step icon={<Send className="h-3.5 w-3.5" />} label="Sent" date={invoice.sent_at ? formatDate(invoice.sent_at) : '—'} done={!!invoice.sent_at} />
                <StepDivider />
                <Step icon={<Wallet className="h-3.5 w-3.5" />} label="Paid" date={invoice.paid_at ? formatDate(invoice.paid_at) : '—'} done={!!invoice.paid_at} />
              </ol>
            ) : (
              <ol className="flex items-center gap-0 self-stretch lg:self-end">
                <Step icon={<Pencil className="h-3.5 w-3.5" />} label="Created" date={formatDate(invoice.issued_at)} done />
                <StepDivider />
                <Step icon={<Send className="h-3.5 w-3.5" />} label="Sent" date={invoice.sent_at ? formatDate(invoice.sent_at) : '—'} done={!!invoice.sent_at} />
                <StepDivider />
                <Step icon={<CheckCircle2 className="h-3.5 w-3.5" />} label="Decision" date={invoice.status === 'accepted' ? 'Accepted' : invoice.status === 'declined' ? 'Declined' : '—'} done={invoice.status === 'accepted' || invoice.status === 'declined'} />
              </ol>
            )}
          </motion.section>

          {/* Cross-links between related documents (036) */}
          {(invoice.related_invoice_id || invoice.converted_from_id) && (
            <section className={[VISUAL.bento, 'p-4 px-5 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-[13px]'].join(' ')}>
              {invoice.related_invoice_id && (
                <span className="text-muted-foreground">
                  Credits invoice{' '}
                  <Link to={`/dashboard/invoices/${invoice.related_invoice_id}`} className="font-semibold text-foreground hover:text-saffron-foreground transition-colors">
                    view original →
                  </Link>
                </span>
              )}
              {invoice.converted_from_id && (
                <span className="text-muted-foreground">
                  Converted from estimate{' '}
                  <Link to={`/dashboard/invoices/${invoice.converted_from_id}`} className="font-semibold text-foreground hover:text-saffron-foreground transition-colors">
                    view estimate →
                  </Link>
                </span>
              )}
            </section>
          )}

          {/* Items */}
          <section className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
            <div className="flex items-center justify-between mb-5">
              <h3 className={VISUAL.dashSection}>Line items</h3>
              <p className="text-[12px] text-muted-foreground">{invoice.items.length} {invoice.items.length === 1 ? 'item' : 'items'}</p>
            </div>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full border-separate border-spacing-0">
                <thead>
                  <tr className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground/80">
                    <th className="text-left py-2.5 px-2 font-semibold">Description</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Qty</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Unit</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Tax</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((it) => (
                    <tr key={it.id} className="border-t border-border/40">
                      <td className="py-3 px-2 align-top text-[14px] text-foreground">
                        {it.description}
                        {it.discount_percent != null && Number(it.discount_percent) > 0 && (
                          <span className="text-muted-foreground text-[12.5px]"> (−{Number(it.discount_percent)}%)</span>
                        )}
                      </td>
                      <td className="py-3 px-2 align-top text-right text-[13.5px] font-mono tabular-nums">{Number(it.quantity)}</td>
                      <td className="py-3 px-2 align-top text-right text-[13.5px] font-mono tabular-nums">{formatMoney(it.unit_price, currency)}</td>
                      <td className="py-3 px-2 align-top text-right text-[13px] text-muted-foreground font-mono">{Number(it.tax_rate)}%</td>
                      <td className="py-3 px-2 align-top text-right text-[14px] font-semibold font-mono tabular-nums">{formatMoney(it.line_total, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-end">
              <dl className="w-full max-w-[280px] space-y-1.5 text-[13.5px]">
                <SumRow label="Subtotal" value={formatMoney(invoice.subtotal, currency)} />
                {invoice.discount_type && Number(invoice.discount_value) > 0 && (
                  <SumRow
                    label={invoice.discount_type === 'percent' ? `Discount (${Number(invoice.discount_value)}%)` : 'Discount'}
                    value={`−${formatMoney(discountAmount(invoice), currency)}`}
                  />
                )}
                <SumRow label="Tax" value={formatMoney(invoice.tax_total, currency)} />
                <div className="mt-3 pt-3 border-t border-border/40 flex items-baseline justify-between gap-3">
                  <span className="text-[12.5px] uppercase tracking-[0.14em] text-muted-foreground/80 font-semibold">Total</span>
                  <span className="font-sans font-semibold text-[20px] tabular-nums tracking-[-0.02em] text-foreground">
                    {formatMoney(invoice.total, currency)}
                  </span>
                </div>
                {docType === 'invoice' && hasMoneyApplied && (
                  <>
                    {Number(invoice.amount_paid ?? 0) > 0 && (
                      <SumRow label="Paid" value={`−${formatMoney(invoice.amount_paid!, currency)}`} />
                    )}
                    {Number(invoice.amount_credited ?? 0) > 0 && (
                      <SumRow label="Credited" value={`−${formatMoney(invoice.amount_credited!, currency)}`} />
                    )}
                    {showDueMath && (
                      <div className="pt-2 border-t border-border/40 flex items-baseline justify-between gap-3">
                        <span className="text-[12.5px] uppercase tracking-[0.14em] text-muted-foreground/80 font-semibold">Amount due</span>
                        <span className="font-sans font-semibold text-[16px] tabular-nums text-foreground">
                          {formatMoney(due, currency)}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </dl>
            </div>
          </section>

          {/* Payments ledger (036) */}
          {docType === 'invoice' && payments.length > 0 && (
            <section className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
              <div className="flex items-center justify-between mb-5">
                <h3 className={VISUAL.dashSection}>Payments</h3>
                <p className="text-[12px] text-muted-foreground">{payments.length} {payments.length === 1 ? 'payment' : 'payments'}</p>
              </div>
              <ul className="divide-y divide-border/40">
                {payments.map((p) => (
                  <li key={p.id} className="flex items-center gap-4 py-3">
                    <div className="h-9 w-9 rounded-xl bg-saffron/12 ring-1 ring-saffron/20 flex items-center justify-center shrink-0">
                      <Receipt className="h-3.5 w-3.5 text-saffron-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13.5px] font-semibold text-foreground">
                        {formatMoney(p.amount, currency)}
                        {p.method && <span className="font-normal text-muted-foreground"> · {p.method.replace(/_/g, ' ')}</span>}
                      </p>
                      {p.note && <p className="text-[12.5px] text-muted-foreground truncate">{p.note}</p>}
                    </div>
                    <span className="text-[12.5px] text-muted-foreground tabular-nums shrink-0">{formatDate(p.paid_at)}</span>
                    {canWrite && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={deletingPaymentId === p.id}
                        aria-label={`Delete payment of ${formatMoney(p.amount, currency)}`}
                        className="rounded-full h-8 px-2.5 text-[12px] gap-1 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeletePayment(p)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {deletingPaymentId === p.id ? 'Deleting…' : 'Delete'}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Notes + Terms */}
          {(invoice.notes || invoice.terms) && (
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {invoice.notes && (
                <NoteCard title="Notes" body={invoice.notes} />
              )}
              {invoice.terms && (
                <NoteCard title="Terms" body={invoice.terms} />
              )}
            </section>
          )}

          {/* Client */}
          {client && (
            <section className={[VISUAL.bento, 'p-6 flex items-center gap-5'].join(' ')}>
              <div className="h-12 w-12 rounded-2xl bg-muted/50 ring-1 ring-border/40 flex items-center justify-center text-[14px] font-bold text-foreground/85 shrink-0">
                {client.name.slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold tracking-[-0.005em] text-foreground truncate">{client.name}</p>
                <p className="text-[12.5px] text-muted-foreground truncate">
                  {client.email ?? 'No email on file'}
                  {client.currency ? ` · bills in ${client.currency}` : ''}
                </p>
              </div>
              <Link to={`/dashboard/clients/${client.id}`} className="text-[12.5px] font-semibold text-foreground hover:text-saffron-foreground transition-colors">
                View client →
              </Link>
            </section>
          )}
        </div>
      </div>

      {/* Side PDF — narrower in the lg→xl band (iPad landscape) so the main
          content keeps breathing room; full width from xl up. */}
      <aside className="w-[360px] 2xl:w-[480px] shrink-0 border-l border-border/40 bg-card/60 hidden xl:flex flex-col">
        <PdfPreviewPanel invoiceId={invoice.id} invoiceNumber={invoice.invoice_number} />
      </aside>

      {/* Dialogs (036) */}
      <RecordPaymentDialog
        open={payDialogOpen}
        onOpenChange={setPayDialogOpen}
        invoice={invoice as Invoice}
        currency={currency}
        onRecorded={(next) => { setInvoice(next); paymentsQ.reload?.(); }}
      />
      <CreditNoteDialog
        open={creditDialogOpen}
        onOpenChange={setCreditDialogOpen}
        invoice={invoice as Invoice}
        currency={currency}
        onIssued={(related) => setInvoice({ ...invoice, ...related })}
      />
    </div>
  );
}

/** Invoice-level discount amount derived from the stored type/value (mirrors the server). */
function discountAmount(inv: { subtotal: string; discount_type?: 'percent' | 'fixed' | null; discount_value?: string | null }): number {
  const sub = Number(inv.subtotal);
  const v = Number(inv.discount_value ?? 0);
  if (!inv.discount_type || !(v > 0)) return 0;
  const amt = inv.discount_type === 'percent' ? sub * (v / 100) : Math.min(v, sub);
  return Math.round((amt + Number.EPSILON) * 100) / 100;
}

function Step({ icon, label, date, done }: { icon: React.ReactNode; label: string; date: string; done: boolean }) {
  return (
    <li className="flex items-center gap-2.5">
      <span
        className={[
          'inline-flex h-9 w-9 items-center justify-center rounded-full ring-1',
          done ? 'bg-saffron text-zinc-950 ring-saffron/40' : 'bg-card text-muted-foreground ring-border/60',
        ].join(' ')}
      >
        {done ? <CheckCircle2 className="h-4 w-4" /> : icon}
      </span>
      <div>
        <p className="text-[11.5px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">{label}</p>
        <p className="text-[12.5px] font-semibold text-foreground tabular-nums">{date}</p>
      </div>
    </li>
  );
}

function StepDivider() {
  return <span aria-hidden className="mx-3 h-px w-6 sm:w-10 bg-border/60" />;
}

function SumRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}

function NoteCard({ title, body }: { title: string; body: string }) {
  return (
    <div className={[VISUAL.bento, 'p-5'].join(' ')}>
      <p className={[VISUAL.eyebrow, 'mb-2'].join(' ')}>{title}</p>
      <p className="text-[13.5px] text-foreground leading-relaxed whitespace-pre-wrap">{body}</p>
    </div>
  );
}
